import React, { useEffect, useState } from 'react';
import { Button, Tooltip } from 'antd';
import { useRefTags } from '@src/hooks';
import { tid } from '@utils/test';
import { dimensionsCategory, dimensionsCategoryOrder } from '@utils/dimension/dimensionCategories';
import useDimensionsMeta from '@src/hooks/useDimensionsMeta';
import {
  ContentWrapper,
  CountText,
  FlexFill,
  FooterWrapper,
  RightPanel,
  RowWrapper,
  StyledDrawer,
} from './styled';
import { CURRENT_PRODUCT_MODE } from '@utils/constants';
import ModalTitleWithSearch from './ModalTitleSearch';
import ModalAnchor from './MetricAnchor';
import CheckboxGroup from './CheckGroup';
import { DIMENSION_LIMIT } from '@store/trends/base/reducer';
import { WarningOutlined } from '@ant-design/icons';

const DimensionPopupModal = ({
  title,
  visible,
  onCancel,
  onOk,
  okText,
  onClear,
  selectedDimensionsList,
  dimensionsCategories,
  onChange,
  searchRef,
}) => {
  const currentDimensionsCategoryOrder = dimensionsCategoryOrder[CURRENT_PRODUCT_MODE];
  const { forceUpdateDimensionsMeta } = useDimensionsMeta();
  const dimensionsListCategory = dimensionsCategory[CURRENT_PRODUCT_MODE];
  const { refs, setRefFn } = useRefTags(currentDimensionsCategoryOrder);
  const [searchText, setSearchText] = useState('');

  const onCancelModal = () => {
    setSearchText('');
    onCancel();
  };

  const getInputListFn = (dimensionsList: any = []) => {
    if (searchText) {
      const st = searchText.toLowerCase();
      return dimensionsList.filter((dimension) => dimension.name.toLowerCase().includes(st));
    }
    return dimensionsList;
  };

  const renderFooterButtons = () => {
    const dimensionCount = selectedDimensionsList.length;
    const disableApply =
      dimensionCount > DIMENSION_LIMIT.max || dimensionCount < DIMENSION_LIMIT.min;
    const disabledApplyTooltipTitle =
      dimensionCount > DIMENSION_LIMIT.max
        ? `Exceeded max limit of ${DIMENSION_LIMIT.max} dimensions`
        : dimensionCount < 1
          ? `Please add at least ${DIMENSION_LIMIT.min} dimension`
          : '';

    return [
      <FooterWrapper key="footer-wrapper" {...tid('footer')}>
        <Button
          key="clear"
          onClick={onClear}
          disabled={dimensionCount < 1}
          style={dimensionCount < 1 ? { opacity: 0.5 } : {}}
        >
          Clear All
        </Button>
        <CountText>
          {disabledApplyTooltipTitle ? (
            <>
              <WarningOutlined style={{ color: '#ff6347' }} className="mr-1" />
              {disabledApplyTooltipTitle}
            </>
          ) : (
            `${dimensionCount} ${dimensionCount > 1 ? 'dimensions' : 'dimension'} selected`
          )}
        </CountText>
        <FlexFill />
        <Button key="discard" onClick={onCancelModal}>
          Cancel
        </Button>
        <Tooltip title={disabledApplyTooltipTitle}>
          <Button
            type="primary"
            key="save"
            disabled={disableApply}
            style={disableApply ? { opacity: 0.5, marginLeft: '8px' } : { marginLeft: '8px' }}
            onClick={onOk}
          >
            {okText}
          </Button>
        </Tooltip>
      </FooterWrapper>,
    ];
  };

  const titleWithSearch = (
    <ModalTitleWithSearch
      placeholder="Search dimension"
      modalTitle={title}
      searchText={searchText}
      setSearchText={setSearchText}
      searchRef={searchRef}
    />
  );

  // skip excluded categories
  const categoryOrder = currentDimensionsCategoryOrder.filter(
    (categoryKey) => categoryKey in dimensionsCategories,
  );

  return (
    <StyledDrawer
      title={titleWithSearch}
      open={visible}
      onClose={onCancelModal}
      footer={renderFooterButtons()}
      width={1024}
      {...tid('dimension-popup')}
    >
      <ContentWrapper {...tid('content')}>
        <div style={{ background: '#F5F5F5' }}>
          <ModalAnchor
            navRefs={refs.current}
            categoryOrder={categoryOrder}
            listCategory={dimensionsListCategory}
          />
        </div>
        <RightPanel>
          {categoryOrder.map((categoryKey) => {
            return (
              <RowWrapper key={categoryKey}>
                <CheckboxGroup
                  categoryKey={categoryKey}
                  listCategory={dimensionsListCategory}
                  inputList={getInputListFn(dimensionsCategories[categoryKey])}
                  onChange={onChange}
                  setRef={setRefFn(categoryKey)}
                  hasSearchText={!!searchText}
                  emptyText={<>No dimensions available</>}
                />
              </RowWrapper>
            );
          })}
        </RightPanel>
      </ContentWrapper>
    </StyledDrawer>
  );
};

export default DimensionPopupModal;
